/**
 * Subservice Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Skeleton,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { PageHeader } from '../../components/common';
import {
  useGetSubServiceQuery,
  useGetInternalSubServicesQuery,
} from '../../store/api/servicesApi';
import { InternalSubService } from '../../types/service';
import { formatCurrency as formatPrice } from '../../utils/pricing';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const SubserviceDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedInternal, setExpandedInternal] = useState(true);
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);

  const { data, isLoading, error } = useGetSubServiceQuery(Number(id));
  const { data: internalData, isLoading: isLoadingInternal } = useGetInternalSubServicesQuery(
    {
      subservice_id: Number(id!),
      page: internalPage + 1,
      page_size: internalRowsPerPage,
    },
    { skip: !id }
  );

  const subservice = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !subservice) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('subservices.detail.notFound')}</Typography>
        <Button onClick={() => navigate('/subservices')} sx={{ mt: 2 }}>
          {t('subservices.buttons.backToSubservices')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={subservice.name}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.subServices'), path: '/subservices' },
          { label: subservice.name },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/subservices')}
        sx={{ mb: 3 }}
      >
        {t('subservices.buttons.backToSubservices')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t('subservices.detail.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('subservices.detail.createdOn', { date: moment(subservice.created_at).format('MMMM DD, YYYY') })}
                  </Typography>
                </Box>
                <Chip
                  label={subservice.is_active ? t('subservices.status.ACTIVE') : t('subservices.status.INACTIVE')}
                  color={subservice.is_active ? 'success' : 'default'}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.nameEn')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {subservice.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.nameAr')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} dir="rtl">
                    {subservice.name_ar || '—'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.service')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {subservice.service_name}
                  </Typography>
                </Box>

                {subservice.slug && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.slug')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {subservice.slug}
                    </Typography>
                  </Box>
                )}

                {subservice.uses_dual_pricing ? (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('subservices.detail.specialistPrice')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        {formatPrice(subservice.specialist_price)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('subservices.detail.consultantPrice')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        {formatPrice(subservice.consultant_price)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('subservices.detail.price')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="primary">
                      {formatPrice(subservice.price)}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.internalSubservices')}
                  </Typography>
                  <Chip
                    label={subservice.internal_subservices_count}
                    size="small"
                    color={subservice.has_internal_subservices ? 'info' : 'default'}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.type')}
                  </Typography>
                  <Chip
                    label={subservice.is_top_level ? t('subservices.type.TOP_LEVEL') : subservice.is_internal ? t('subservices.type.INTERNAL') : t('subservices.type.REGULAR')}
                    size="small"
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.descriptionEn')}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {subservice.description?.trim() ? subservice.description : '—'}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('subservices.detail.descriptionAr')}
                  </Typography>
                  <Typography variant="body1" dir="rtl" sx={{ whiteSpace: 'pre-wrap' }}>
                    {subservice.description_ar?.trim() ? subservice.description_ar : '—'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('subservices.detail.icon')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Avatar
                  src={subservice.icon || undefined}
                  variant="rounded"
                  sx={{ width: 300, height: 300, maxWidth: '100%', maxHeight: '100%' }}
                >
                  <ImageIcon sx={{ fontSize: 150 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Internal Subservices Section - Full Width */}
      {subservice.has_internal_subservices && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {t('subservices.detail.internalWithCount', { total: internalData?.count || 0 })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/subservices/${subservice.id}/internal/create`)}
                >
                  {t('subservices.buttons.addInternal')}
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setExpandedInternal(!expandedInternal)}
                >
                  {expandedInternal ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Collapse in={expandedInternal} timeout="auto" unmountOnExit>
              {isLoadingInternal ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : internalData?.results && internalData.results.length > 0 ? (
                <Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                    <Table size="small" sx={{ width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('subservices.cols.name')}</TableCell>
                          <TableCell>{t('subservices.cols.price')}</TableCell>
                          <TableCell>{t('subservices.cols.status')}</TableCell>
                          <TableCell align="right">{t('common.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {internalData.results.map((internal: InternalSubService) => (
                          <TableRow key={internal.id} hover>
                            <TableCell>
                              <Box
                                onClick={() => navigate(`/internal-subservices/${internal.id}`)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  color="primary.main"
                                >
                                  {internal.name}
                                </Typography>
                              </Box>
                              {internal.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {internal.description.length > 40
                                    ? `${internal.description.substring(0, 40)}...`
                                    : internal.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {internal.uses_dual_pricing
                                  ? `${formatPrice(internal.specialist_price)} / ${formatPrice(internal.consultant_price)}`
                                  : formatPrice(internal.price)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={internal.is_active ? t('subservices.status.ACTIVE') : t('subservices.status.INACTIVE')}
                                size="small"
                                color={internal.is_active ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/subservices/${subservice.id}/internal/${internal.id}/edit`)}
                                title={t('subservices.buttons.editInternal')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {internalData.count > internalRowsPerPage && (
                    <TablePagination
                      component="div"
                      count={internalData.count}
                      page={internalPage}
                      onPageChange={(_, newPage) => setInternalPage(newPage)}
                      rowsPerPage={internalRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setInternalRowsPerPage(parseInt(e.target.value, 10));
                        setInternalPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" gutterBottom>
                    {t('subservices.empty.internal')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/subservices/${subservice.id}/internal/create`)}
                    sx={{ mt: 2 }}
                  >
                    {t('subservices.buttons.addInternal')}
                  </Button>
                </Box>
              )}
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Show button to create first internal subservice if subservice doesn't have any yet */}
      {!subservice.has_internal_subservices && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('subservices.empty.noInternalYet')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/subservices/${subservice.id}/internal/create`)}
                sx={{ mt: 2 }}
              >
                {t('subservices.buttons.addInternal')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SubserviceDetailPage;

